import { BeheerVos, useCreateBeheerVos, useSearchVosByEmail, useUpdateBeheerVos } from "@/entities/medewerkers-op-station";
import { StationMultiSelect } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldInput,
    RadioGroup,
    RadioGroupItem,
    Skeleton,
} from "@/shared/components";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider.tsx";
import { useDebouncedCallback } from "@/shared/hooks/use-debounced-callback.ts";
import { cn } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
    entraId: z.string().nullable(),
    email: z.string().optional(),
    inzetStationcodes: z.array(z.string()),
});

type UpsertVosData = z.infer<typeof schema>;

type UpsertVosDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vos: BeheerVos | null;
};

export function UpsertVosDialog({ open, onOpenChange, vos }: UpsertVosDialogProps) {
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const createVos = useCreateBeheerVos();
    const updateVos = useUpdateBeheerVos();
    const searchVosByEmail = useSearchVosByEmail();

    const isEdit = !!vos;
    const form = useForm<UpsertVosData>({
        resolver: zodResolver(schema),
        defaultValues: {
            entraId: vos?.entraId ?? null,
            inzetStationcodes: vos?.inzetStationcodes ?? [],
        },
    });

    const debouncedSearch = useDebouncedCallback((email: string) => {
        if (email.trim() !== "") {
            searchVosByEmail.mutate({ email });
        } else {
            searchVosByEmail.reset();
        }
    }, 300);

    const closeAndReset = () => {
        onOpenChange(false);
        form.reset();
    };

    const onSubmit = (data: UpsertVosData) => {
        if (data.entraId === null) return;
        const options = {
            onSuccess: () => closeAndReset(),
        };
        if (isEdit) {
            updateVos.mutate({ entraId: data.entraId, inzetStationcodes: data.inzetStationcodes }, options);
        } else {
            createVos.mutate({ entraId: data.entraId, inzetStationcodes: data.inzetStationcodes }, options);
        }
    };

    const emailSearchValue = form.watch("email");
    const selectedEntraId = form.watch("entraId");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={dialogRef}>
                <DialogContainer className="min-h-[168px] min-w-1/2 sm:max-w-[540px]" ref={dialogRef}>
                    <DialogHeader>
                        <DialogTitle>{isEdit ? "Vrijwilliger bewerken" : "Nieuwe vrijwilliger registreren"}</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                            <DialogBody className="flex flex-col gap-2">
                                <DialogDescription>
                                    {isEdit
                                        ? "Bewerk de stations waarop deze vrijwilliger ingezet kan worden"
                                        : "Zoek een medewerker op basis van email en voeg de stations toe waarop deze ingezet kan worden"}
                                </DialogDescription>
                                {!vos && (
                                    <>
                                        <FormFieldInput
                                            type="email"
                                            form={form}
                                            name="email"
                                            label="E-mail adres (@ns.nl)"
                                            onValueChange={(value) => debouncedSearch(value as string)}
                                        />
                                        <VosSearchResults
                                            error={searchVosByEmail.error}
                                            isPending={searchVosByEmail.isPending}
                                            results={searchVosByEmail.data ?? []}
                                            hasSearched={emailSearchValue !== ""}
                                            selectedEntraId={selectedEntraId}
                                            setSelectedEntraId={(value: string) => form.setValue("entraId", value)}
                                        />
                                    </>
                                )}
                                {vos && <VosSearchResults results={[vos]} hasSearched selectedEntraId={vos.entraId} />}

                                <StationMultiSelect
                                    form={form}
                                    name="inzetStationcodes"
                                    label="Inzet op"
                                    placeholder="Selecteer station(s)"
                                    badgeLabel="value"
                                    nlStationsOnly
                                />
                            </DialogBody>
                            <DialogFooter>
                                {createVos.error && <ErrorDisplay error={createVos.error} />}
                                {updateVos.error && <ErrorDisplay error={updateVos.error} />}
                                <Button type="button" variant="tertiary" onClick={closeAndReset}>
                                    Annuleren
                                </Button>
                                <Button type="submit" disabled={createVos.isPending || updateVos.isPending}>
                                    Opslaan
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}

type VosSearchResultsProps = {
    error?: Error | null;
    isPending?: boolean;
    results: BeheerVos[];
    hasSearched: boolean;
    selectedEntraId: string | null;
    setSelectedEntraId?: (id: string) => void;
};

function VosSearchResults({ error, isPending, results, selectedEntraId, setSelectedEntraId }: VosSearchResultsProps) {
    if (error) {
        return <ErrorDisplay error={error} />;
    }

    if (isPending) {
        return <Skeleton className="h-10 w-full" />;
    }

    if (results.length > 0) {
        return (
            <div className="p-1">
                <RadioGroup value={selectedEntraId} onValueChange={setSelectedEntraId}>
                    {results.map((vos) => (
                        <VosRadioItem key={`vos-radio-${vos.entraId}`} item={vos} selected={selectedEntraId === vos.entraId} />
                    ))}
                </RadioGroup>
            </div>
        );
    }

    return null;
}

type VosRadioItemProps = {
    item: BeheerVos;
    selected: boolean;
};
function VosRadioItem({ item, selected }: VosRadioItemProps) {
    return (
        <div
            className={cn(
                "border-subtle grid grid-cols-[min-content_1fr] items-center gap-2 border-t border-b p-2",
                selected && "bg-accent-blue-subtle"
            )}
        >
            <RadioGroupItem value={item.entraId} id={item.entraId} data-testid={`select-vos-${item.entraId}`} />
            <label htmlFor={item.entraId} className="text-content-default text-m flex flex-row gap-4">
                <span className="text-m leading-l">{item.naam}</span>
                <span className="text-m leading-l">{item.email}</span>
            </label>
        </div>
    );
}
